/* eslint-disable no-param-reassign */
import React, { createContext, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelected } from '@watchable/store-react';

import { LeavesContext } from 'context/leaves';

import { OptimisticStateReturn, useOptimisticState, DispatchOptions } from 'hooks/useOptimisticState';
import { getLeafById, getParentLeafById } from 'modules/leaves';
import * as optimisticEvents from 'modules/optimisticEvents';

import { AppLeaf, AppDeviceLeaf, AppGroupLeaf } from 'types/common/leaf';
import { OptimisticAction } from 'types/actions/optimistic';
import { deviceLeafReducer } from 'context/reducers/deviceLeaf';
import { groupLeafReducer } from 'context/reducers/groupLeaf';

// Reducer map based on leaf type
const reducerMap = {
  device: deviceLeafReducer,
  group: groupLeafReducer,
} as const;

type OptimisticLeafReturn = OptimisticStateReturn<AppLeaf, OptimisticAction>;

interface LeafStateContext extends Omit<OptimisticLeafReturn, 'state'> {
  leaf: OptimisticLeafReturn['state'];
}

export const LeafStateContext = createContext<LeafStateContext>({} as LeafStateContext);

// Generic hook for type-safe leaf state access
export function useLeafState<T extends AppLeaf = AppLeaf>(): Omit<
  OptimisticStateReturn<T, OptimisticAction>,
  'state'
> & {
  leaf: T;
} {
  const context = useContext(LeafStateContext);
  if (!context) {
    throw new Error('useLeafState must be used within a LeafStateProvider');
  }
  return context as Omit<OptimisticStateReturn<T, OptimisticAction>, 'state'> & { leaf: T };
}

// Convenience hooks for type-specific usage
export const useDeviceLeaf = () => useLeafState<AppDeviceLeaf>();
export const useGroupLeaf = () => useLeafState<AppGroupLeaf>();

interface LeafStateProviderProps {
  leafId: AppLeaf['id'];
  children: React.ReactNode;
  NoLeafComponent?: React.FC;
}

export const LeafStateProvider = ({ leafId, children, NoLeafComponent }: LeafStateProviderProps) => {
  const { leafStore } = useContext(LeavesContext);
  const leaf = useSelected<Record<string, AppLeaf>, AppLeaf | null>(leafStore, (leaves) => getLeafById(leaves, leafId));
  const leaves = leafStore.read();

  // Light-specific store merge logic - for future promise-based light operations
  const mergeToStore = useCallback(
    (_result: Partial<AppLeaf>, action: OptimisticAction) => {
      switch (action.type) {
        case 'SET_BRIGHTNESS':
        case 'SET_POWER':
        case 'SET_COLOR':
          // All current light actions are fire-and-forget via WebSocket
          // No store merge needed - WebSocket updates handle this
          // This is here for future promise-based light operations
          break;

        default:
          break;
      }
    },
    [leafStore, leafId],
  );

  // Get the appropriate reducer based on leaf type
  const reducer = leaf ? reducerMap[leaf.type] : reducerMap.device;
  // console.log(`[${leafId}] Using reducer for leaf type:`, leaf?.type, 'reducer:', reducer === deviceLeafReducer ? 'device' : 'group');

  const {
    state: leafState,
    isOptimistic,
    dispatch: originalDispatch,
    dispatchAsync,
    engageLock,
    disengageLock,
    error,
  } = useOptimisticState<AppLeaf, OptimisticAction>({
    actualState: leaf || ({} as AppLeaf),
    reducer,
    mergeToStore,
  });

  // Enhanced dispatch that emits global events
  const dispatch = useCallback(
    (action: OptimisticAction, command?: () => void, options?: DispatchOptions) => {
      const actionWithDefaults = {
        ...action,
        origin: action.origin || { type: leaf?.type || 'device', trigger: 'user' }, // Default to user action
      };

      // Apply optimistic update locally
      originalDispatch(actionWithDefaults, command, options);

      // Handle auto-turn-on side effects for brightness and color actions
      if (actionWithDefaults.type === 'SET_BRIGHTNESS' || actionWithDefaults.type === 'SET_COLOR') {
        if (actionWithDefaults.origin.type === 'group' && leaf?.type === 'group') {
          // Group action: emit auto-turn-on power actions to all member devices
          Object.keys(leaf.memberLeaves).forEach((memberId) => {
            optimisticEvents.dispatchEvent({
              leafId: memberId,
              data: {
                action: {
                  type: 'SET_POWER',
                  payload: 'on',
                  origin: { type: 'device', trigger: 'side-effect' },
                },
              },
            });
          });
        } else if (actionWithDefaults.origin.type === 'device' && leaf?.type === 'device') {
          // Device action: emit auto-turn-on power action to self
          optimisticEvents.dispatchEvent({
            leafId,
            data: {
              action: {
                type: 'SET_POWER',
                payload: 'on',
                origin: { type: 'device', trigger: 'side-effect' },
              },
            },
          });
        }
      }

      // Only emit global events for user-originated actions to prevent loops
      if (actionWithDefaults.origin.trigger === 'user') {
        optimisticEvents.dispatchEvent({
          leafId,
          data: { action: actionWithDefaults, options },
        });
      }
    },
    [originalDispatch, leaf, leafId],
  );

  // Use refs to ensure handlers have access to latest values
  const currentDispatchRef = useRef(originalDispatch);
  const currentIsOptimisticRef = useRef(isOptimistic);

  currentDispatchRef.current = originalDispatch;
  currentIsOptimisticRef.current = isOptimistic;

  // Listen for parent and member leaf changes
  useEffect(() => {
    if (!leaf || !leaves) return;

    const subscriptions: Array<() => void> = [];

    // Listen to parent (if exists) - for receiving group actions from above
    const parentGroup = getParentLeafById(leaves, leafId);
    if (parentGroup) {
      const handleParentAction = (e: CustomEvent) => {
        const { data } = e.detail;
        if (data?.action && data.action.origin.type === 'group') {
          // Apply parent group action with lock protection (mark as side-effect to prevent loops)
          // Use the same options as the original action for consistent timing
          dispatch(
            { ...data.action, origin: { ...data.action.origin, trigger: 'side-effect' } },
            undefined,
            data.options,
          );
        }
      };
      optimisticEvents.subscribe(parentGroup.id, handleParentAction);
      subscriptions.push(() => optimisticEvents.unsubscribe(parentGroup.id, handleParentAction));
    }

    // Listen to members (if exists) - for receiving device changes from below
    if (leaf.type === 'group') {
      const memberDeviceIds = Object.keys(leaf.memberLeaves);

      const handleMemberChange = (e: CustomEvent) => {
        const { leafId: memberId, data } = e.detail;
        if (memberDeviceIds.includes(memberId) && data?.action && data.action.origin.type === 'device') {
          // Add the originating device ID to the action so the reducer knows which device changed
          const actionWithDeviceId = {
            ...data.action,
            origin: { ...data.action.origin, trigger: 'side-effect' as const, id: memberId },
          };
          // Update member device and recalculate group state with lock protection
          // Use the same options as the original action for consistent timing
          dispatch(actionWithDeviceId, undefined, data.options);
        }
      };

      memberDeviceIds.forEach((memberId) => {
        optimisticEvents.subscribe(memberId, handleMemberChange);
        subscriptions.push(() => optimisticEvents.unsubscribe(memberId, handleMemberChange));
      });
    }

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [leafId, leaf?.id, leaf?.type]); // Only essential dependencies

  if (!leaf || !Object.keys(leafState).length) {
    return NoLeafComponent ? <NoLeafComponent /> : null;
  }

  const contextValue = useMemo(
    () => ({
      leaf: leafState,
      isOptimistic,
      dispatch,
      dispatchAsync,
      engageLock,
      disengageLock,
      error,
    }),
    [leafState, isOptimistic, dispatch, dispatchAsync, engageLock, disengageLock, error],
  );

  return <LeafStateContext.Provider value={contextValue}>{children}</LeafStateContext.Provider>;
};
